<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import dayjs, { type Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { stores } from '@/util/stores.js';
import { useToast } from '@/composables/useToast.ts';
import AvailabilityForm from './AvailabilityForm.vue';
import AvailabilityEntryDetails from './AvailabilityEntryDetails.vue';
import { getAvailabilityRestrictions } from '@/util/availabilityDisplay';
import { BLOCK_TYPES, getBlockingBlockTypeForScope, isAvailabilityBlockType, type AvailabilityBlockType, type AvailabilityEntry } from '@/types/availability';

dayjs.extend(isoWeek);

interface MatchRecord extends Record<string, unknown> {
	group?: {
		groupNumber?: number | null;
		shortName?: string | null;
		name?: string | null;
	} | null;
	visibility?: string | null;
	matchTime?: string | null;
	matchEndTime?: string | null;
	matchNumber?: string | number | null;
}

interface GroupedMatchEntry {
	groupNumber: number;
	groupName: string;
	matches: MatchRecord[];
	hasHidden: boolean;
	earliestTime: Dayjs | null;
	latestEndTime: Dayjs | null;
	matchCount: number;
	timeRange: string;
}

const props = withDefaults(
	defineProps<{
		visible?: boolean;
		selectedDates?: string[];
		mobileMode?: boolean;
	}>(),
	{
		visible: false,
		selectedDates: () => [],
		mobileMode: false
	}
);

const emit = defineEmits<{
	close: [];
	saved: [value?: unknown];
}>();

const toast = useToast();
const isVisible = computed(() => props.visible ?? false);
const selectedDates = computed(() => props.selectedDates ?? []);
const mobileMode = computed(() => props.mobileMode ?? false);

// State
const saving = ref(false);
const selectedScope = ref<AvailabilityBlockType>(BLOCK_TYPES.AVAILABLE_GLOBAL);
const selectedGroupNumber = ref<number | null>(null);
const groupsLoading = ref(false);
const showGroupValidation = ref(false);
const panelView = ref<'quick' | 'form'>('quick');

// Groups for dropdown
const userGroups = computed(() => {
	const raw: Array<any> = stores.usersStore.groups || [];
	return raw
		.map((g) => g?.group || g)
		.filter((g) => g && (g.groupNumber || g.groupNumber === 0))
		.filter((g) => g.groupNumber !== 0)
		.map((g) => ({
			groupNumber: Number(g.groupNumber),
			name: g.shortName ? `${g.shortName} — ${g.name}` : g.name
		}));
});

// Watch scope changes to reset group selection
watch(selectedScope, (newScope) => {
	if (newScope !== BLOCK_TYPES.AVAILABLE_GROUP) {
		selectedGroupNumber.value = null;
		showGroupValidation.value = false;
	}
});

watch(
	() => isVisible.value,
	(open) => {
		if (!open) panelView.value = 'quick';
	}
);

watch(
	() => selectedDates.value.join('|'),
	() => {
		panelView.value = 'quick';
	}
);

// Load groups on mount if needed
onMounted(async () => {
	if (!stores.usersStore.groups || stores.usersStore.groups.length === 0) {
		try {
			groupsLoading.value = true;
			await stores.usersStore.usersGroups();
		} finally {
			groupsLoading.value = false;
		}
	}
});

// Day name mapping for ISO weekday (1=Mon, 7=Sun)
const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const dayNamesShort = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Computed
const dateRange = computed(() => {
	if (selectedDates.value.length === 0) return null;

	const sorted = [...selectedDates.value].sort();
	const start = dayjs(sorted[0]);
	const end = dayjs(sorted[sorted.length - 1]);

	const dayOfWeekCounts: Record<number, number> = {};
	for (const dateStr of selectedDates.value) {
		const d = dayjs(dateStr);
		const dow = d.isoWeekday();
		dayOfWeekCounts[dow] = (dayOfWeekCounts[dow] || 0) + 1;
	}

	const daysSelected = Object.entries(dayOfWeekCounts)
		.sort(([a], [b]) => Number(a) - Number(b))
		.map(([dow, count]) => ({
			day: dayNames[Number(dow) - 1],
			dayShort: dayNamesShort[Number(dow) - 1],
			count
		}));

	if (start.isSame(end, 'day')) {
		return {
			single: true,
			formatted: start.format('dddd, MMMM D'),
			start: start.format('YYYY-MM-DD'),
			end: end.format('YYYY-MM-DD'),
			daysSelected,
			dayCount: 1
		};
	}

	const isWeekendOnly = daysSelected.every((d) => d.day === 'Saturday' || d.day === 'Sunday');
	let formatted = `${start.format('MMM D')} - ${end.format('MMM D')}`;

	return {
		single: false,
		formatted,
		dayCount: selectedDates.value.length,
		start: start.format('YYYY-MM-DD'),
		end: end.format('YYYY-MM-DD'),
		daysSelected,
		isWeekendOnly
	};
});

const pastDatesInfo = computed(() => {
	if (selectedDates.value.length === 0) return { allPast: false, pastCount: 0, futureCount: 0 };

	const today = dayjs().startOf('day');
	let pastCount = 0;
	let futureCount = 0;

	for (const dateStr of selectedDates.value) {
		const d = dayjs(dateStr).startOf('day');
		if (d.isBefore(today)) {
			pastCount++;
		} else {
			futureCount++;
		}
	}

	return {
		allPast: futureCount === 0 && pastCount > 0,
		somePast: pastCount > 0 && futureCount > 0,
		pastCount,
		futureCount
	};
});

const futureDates = computed(() => {
	const today = dayjs().startOf('day');
	return selectedDates.value.filter((dateStr) => {
		const d = dayjs(dateStr).startOf('day');
		return !d.isBefore(today);
	});
});

const matchesInRange = computed(() => {
	const matches: MatchRecord[] = [];
	const now = dayjs();
	for (const dateStr of selectedDates.value) {
		const day = dayjs(dateStr).startOf('day');
		if (day.isBefore(now.startOf('day'))) continue;

		const suggestion = stores.availabilityStore.suggestions.find((s) => dayjs(s.date).format('YYYY-MM-DD') === dateStr);
		if (suggestion?.matches) {
			const filtered = (suggestion.matches as MatchRecord[]).filter((m) => {
				const matchDate = m['matchDate'];
				const mDateStr = matchDate ? dayjs(matchDate as string | Date).format('YYYY-MM-DD') : dateStr;
				const mDay = dayjs(mDateStr).startOf('day');
				if (mDay.isAfter(now.startOf('day'))) return true;
				if (mDay.isSame(now.startOf('day'))) {
					const matchTime = m['matchTime'];
					if (!matchTime) return true;
					const dt = dayjs(`${mDateStr}T${String(matchTime)}`);
					return dt.isSame(now) || dt.isAfter(now);
				}
				return false;
			});
			matches.push(...filtered);
		}
	}
	return matches;
});

const totalMatchCount = computed(() => {
	let total = 0;
	for (const dateStr of selectedDates.value) {
		const suggestion = stores.availabilityStore.suggestions.find((s) => dayjs(s.date).format('YYYY-MM-DD') === dateStr);
		if (suggestion?.matchCount) {
			total += suggestion.matchCount;
		}
	}
	return total || matchesInRange.value.length;
});

const dateRangeDaysSelected = computed(() => dateRange.value?.daysSelected ?? []);

const groupedMatches = computed(() => {
	if (!matchesInRange.value || matchesInRange.value.length === 0) return [];

	const groupMap = new Map<number, Omit<GroupedMatchEntry, 'matchCount' | 'timeRange'>>();

	for (const match of matchesInRange.value) {
		const groupNumber = match.group?.groupNumber ?? 0;
		const groupName = match.group?.shortName || match.group?.name || 'Unknown Group';
		const visibility = match.visibility || 'PUBLIC';

		if (!groupMap.has(groupNumber)) {
			groupMap.set(groupNumber, {
				groupNumber,
				groupName,
				matches: [],
				hasHidden: false,
				earliestTime: null,
				latestEndTime: null
			});
		}

		const entry = groupMap.get(groupNumber);
		if (!entry) continue;
		entry.matches.push(match);

		if (visibility === 'HIDDEN') {
			entry.hasHidden = true;
		}

		const matchTime = match.matchTime ? dayjs(`2000-01-01T${match.matchTime}`) : null;
		const matchEndTime = match.matchEndTime ? dayjs(`2000-01-01T${match.matchEndTime}`) : null;

		if (matchTime && (!entry.earliestTime || matchTime.isBefore(entry.earliestTime))) {
			entry.earliestTime = matchTime;
		}
		if (matchEndTime && (!entry.latestEndTime || matchEndTime.isAfter(entry.latestEndTime))) {
			entry.latestEndTime = matchEndTime;
		} else if (matchTime && !matchEndTime && (!entry.latestEndTime || matchTime.isAfter(entry.latestEndTime))) {
			entry.latestEndTime = matchTime.add(2, 'hour');
		}
	}

	return Array.from(groupMap.values())
		.map(
			(g): GroupedMatchEntry => ({
				...g,
				matchCount: g.matches.length,
				timeRange: formatTimeRange(g.earliestTime, g.latestEndTime)
			})
		)
		.sort((a, b) => {
			if (a.earliestTime && b.earliestTime) {
				return a.earliestTime.isBefore(b.earliestTime) ? -1 : 1;
			}
			return 0;
		});
});

function formatTimeRange(start: Dayjs | null, end: Dayjs | null) {
	if (!start) return '';
	const startStr = start.format('h:mm A');
	if (!end || start.isSame(end)) return startStr;
	return `${startStr} – ${end.format('h:mm A')}`;
}

function getAvailabilityEntryKey(entry: AvailabilityEntry): string | number {
	if (entry.id != null) return entry.id;

	const groupNumber = entry.group?.groupNumber ?? 'global';
	const clientNumber = entry.client?.clientNumber ?? 'global';
	return [entry.startDate, entry.endDate, entry.startTime ?? 'all-day', entry.endTime ?? 'all-day', entry.blockType ?? 'unknown', groupNumber, clientNumber, entry.notes ?? '', entry.createdOn ?? '', entry.updatedOn ?? '', entry.source ?? ''].join(
		'|'
	);
}

const existingAvailability = computed(() => {
	const entries: AvailabilityEntry[] = [];
	for (const dateStr of selectedDates.value) {
		const dayEntries = stores.availabilityStore.blocks.filter((b) => {
			if (!isAvailabilityBlockType(b.blockType)) return false;
			const start = dayjs(b.startDate);
			const end = dayjs(b.endDate);
			const date = dayjs(dateStr);
			return date.isSame(start, 'day') || date.isSame(end, 'day') || (date.isAfter(start) && date.isBefore(end));
		});
		entries.push(...dayEntries);
	}
	const seen = new Set<string | number>();
	return entries.filter((entry) => {
		const entryId = getAvailabilityEntryKey(entry);
		if (seen.has(entryId)) return false;
		seen.add(entryId);
		return true;
	});
});

const hasExistingAvailability = computed(() => existingAvailability.value.length > 0);

const selectedBlocks = computed(() => {
	const { blocks } = getAvailabilityRestrictions(stores.availabilityStore.blocks);
	return blocks.filter((entry) =>
		selectedDates.value.some((date) => {
			const day = dayjs(date);
			return !day.isBefore(dayjs(entry.startDate), 'day') && !day.isAfter(dayjs(entry.endDate), 'day');
		})
	);
});

const selectedEntries = computed(() => [...selectedBlocks.value, ...existingAvailability.value]);
const selectedBlockIds = computed(() => [...new Set(selectedEntries.value.map((entry) => entry.id).filter((id): id is number => typeof id === 'number' && Number.isInteger(id) && id > 0))]);
const hasMultiDayBlocks = computed(() => selectedEntries.value.some((entry) => entry.recurring || !dayjs(entry.startDate).isSame(dayjs(entry.endDate), 'day')));
const canClearBlocks = computed(() => selectedDates.value.length > 0 && selectedBlockIds.value.length > 0);

const canSave = computed(() => {
	if (futureDates.value.length === 0) {
		return false;
	}
	return !(selectedScope.value === BLOCK_TYPES.AVAILABLE_GROUP && (groupsLoading.value || userGroups.value.length === 0 || !selectedGroupNumber.value));
});

// Methods
function close() {
	emit('close');
}

async function quickAdd(timeOption: 'all' | 'am' | 'pm') {
	if (selectedScope.value === BLOCK_TYPES.AVAILABLE_GROUP && !selectedGroupNumber.value) {
		showGroupValidation.value = true;
		return;
	}
	if (saving.value || !canSave.value) return;
	saving.value = true;
	const dates = [...selectedDates.value];

	const options: {
		scope: AvailabilityBlockType;
		allDay: boolean;
		groupNumber: number | null;
		startTime?: string;
		endTime?: string;
	} = {
		scope: selectedScope.value,
		allDay: timeOption === 'all',
		groupNumber: selectedScope.value === BLOCK_TYPES.AVAILABLE_GROUP ? selectedGroupNumber.value : null
	};

	if (timeOption === 'am') {
		options.allDay = false;
		options.startTime = '06:00:00';
		options.endTime = '12:00:00';
	} else if (timeOption === 'pm') {
		options.allDay = false;
		options.startTime = '12:00:00';
		options.endTime = '23:00:00';
	}

	// Dismiss immediately so the calendar remains responsive while the request completes.
	// Capture the dates first because closing clears the parent's selection.
	close();
	const result = await stores.availabilityStore.quickAddAvailability(dates, options);

	if (result.success) {
		toast.add({
			severity: 'success',
			summary: 'Availability Added',
			detail: `Marked ${dates.length} day${dates.length > 1 ? 's' : ''} as available`,
			life: 3000
		});
		emit('saved', result.data);
	} else {
		toast.add({
			severity: 'error',
			summary: 'Could not add availability',
			detail: result.error || 'Failed to add availability',
			life: 5000
		});
	}

	saving.value = false;
}

async function markAsBlocked() {
	if (saving.value) return;
	if (selectedScope.value === BLOCK_TYPES.AVAILABLE_GROUP && !selectedGroupNumber.value) {
		showGroupValidation.value = true;
		return;
	}
	const blockType = getBlockingBlockTypeForScope(selectedScope.value === BLOCK_TYPES.AVAILABLE_GROUP ? 'group' : 'global');
	if (!dateRange.value) {
		return;
	}

	const dates = [...new Set(selectedDates.value)].sort();
	saving.value = true;
	// Dismiss immediately; the request and calendar refresh continue in the background.
	close();
	const result = await stores.availabilityStore.saveBlocks(
		dates.map((date) => ({
			startDate: date,
			endDate: date,
			blockType,
			allDay: true,
			groupNumber: selectedScope.value === BLOCK_TYPES.AVAILABLE_GROUP ? selectedGroupNumber.value : null
		}))
	);

	if (result.success) {
		toast.add({
			severity: 'success',
			summary: 'Days Blocked',
			detail: `Marked ${dates.length} day${dates.length > 1 ? 's' : ''} as blocked`,
			life: 3000
		});
		emit('saved', result.data);
	} else {
		toast.add({
			severity: 'error',
			summary: 'Could not block dates',
			detail: result.error || 'Failed to block dates',
			life: 5000
		});
	}

	saving.value = false;
}

async function clearSelectedBlocks() {
	if (saving.value || stores.availabilityStore.saving || !canClearBlocks.value) return;
	const ids = [...selectedBlockIds.value];
	saving.value = true;
	close();
	try {
		const result = await stores.availabilityStore.deleteBlocks(ids);
		if (result.success) {
			toast.add({
				severity: 'success',
				summary: 'Blocks cleared',
				detail: `Removed ${ids.length} availability ${ids.length === 1 ? 'entry' : 'entries'} from your calendar.`,
				life: 3000
			});
		} else {
			toast.add({ severity: 'error', summary: 'Could not clear all blocks', detail: result.error || 'Please try again to clear the remaining blocks.', life: 5000 });
		}
		if (result.success || result.data?.length) emit('saved');
	} catch {
		toast.add({ severity: 'error', summary: 'Could not clear blocks', detail: 'Please try again.', life: 5000 });
	} finally {
		saving.value = false;
	}
}

function openCustomForm() {
	if (selectedDates.value.length === 0) return;
	panelView.value = 'form';
}

function closeCustomForm() {
	panelView.value = 'quick';
}

function handleHeaderBack() {
	if (panelView.value === 'form') {
		closeCustomForm();
		return;
	}
	close();
}

function handleFormSaved() {
	emit('saved');
	close();
}

function handleFormCancelled() {
	closeCustomForm();
}

defineExpose({
	openCustomForm,
	closeCustomForm
});
</script>

<template>
	<Transition name="panel-slide">
		<div
			v-if="visible && selectedDates.length > 0"
			class="quick-panel absolute top-0 right-0 bottom-0 w-[360px] max-w-full bg-[var(--egs-content-background)] border-l border-surface flex flex-col z-10 shadow-[-4px_0_16px_rgba(0,0,0,0.08)]"
			:class="{ '!fixed !left-0 !right-0 !bottom-0 !top-auto !w-full !max-h-[80vh] !border-l-0 !border-t !border-surface !rounded-t-2xl !shadow-[0_-4px_24px_rgba(0,0,0,0.15)]': mobileMode }"
		>
			<!-- Header -->
			<div class="flex items-center gap-3 py-4 px-5 border-b border-surface">
				<button class="flex items-center justify-center w-9 h-9 border-none rounded-lg bg-[var(--egs-content-hover)] text-color cursor-pointer transition-all hover:bg-[var(--egs-content-hover)]" @click="handleHeaderBack">
					<font-awesome-icon :icon="panelView === 'form' || mobileMode ? ['fas', 'arrow-left'] : ['fas', 'xmark']" />
				</button>
				<div class="flex flex-col">
					<span class="font-semibold text-base text-color">{{ dateRange?.formatted }}</span>
					<span v-if="!dateRange?.single" class="text-[0.8125rem] text-color-secondary">
						{{ dateRange?.dayCount }} days
						<span v-if="dateRangeDaysSelected.length > 1" class="text-xs text-color-secondary font-normal ml-1">
							(<span v-for="(d, idx) in dateRangeDaysSelected" :key="`${d.day}-${idx}`">{{ d.count }} {{ d.dayShort }}{{ d.count > 1 ? 's' : '' }}<span v-if="idx < dateRangeDaysSelected.length - 1">, </span></span>)
						</span>
					</span>
				</div>
			</div>

			<div class="flex-1 overflow-y-auto py-4 px-5 min-h-0">
				<!-- Embedded custom form (reuses QuickActionPanel shell) -->
				<div v-if="panelView === 'form'" class="flex flex-col gap-2.5">
					<div class="text-sm font-bold text-color">Custom availability</div>
					<AvailabilityForm mode="create" :initialData="null" :preselectedDates="selectedDates" @saved="handleFormSaved" @cancelled="handleFormCancelled" />
				</div>

				<!-- Matches Section - Grouped by group with time ranges -->
				<div v-else-if="totalMatchCount > 0" class="mb-5">
					<div class="flex items-center gap-2 font-medium text-color mb-2.5">
						<font-awesome-icon :icon="['fas', 'futbol']" class="text-base" />
						<span>{{ totalMatchCount }} match{{ totalMatchCount !== 1 ? 'es' : '' }} {{ dateRange?.single ? 'this day' : 'in range' }}</span>
					</div>
					<div class="bg-[var(--egs-content-hover)] rounded-lg py-2.5 px-3">
						<div v-for="group in groupedMatches" :key="group.groupNumber" class="py-2 border-b border-surface last:border-b-0">
							<div class="flex items-center gap-1.5">
								<span class="text-[0.8125rem] font-medium text-color">{{ group.groupName }}</span>
								<span class="text-xs text-color-secondary">({{ group.matchCount }})</span>
								<span v-if="group.hasHidden" class="inline-flex items-center gap-1 py-0.5 px-1.5 text-[0.65rem] text-orange-600 bg-orange-500/[0.12] rounded ml-auto">
									<font-awesome-icon :icon="['fas', 'eye-slash']" />
									Hidden
								</span>
							</div>
							<div class="text-xs text-color-secondary mt-0.5">{{ group.timeRange }}</div>
							<!-- Expandable match numbers -->
							<details v-if="!group.hasHidden && group.matches.length > 0" class="mt-1.5">
								<summary class="text-[0.7rem] text-primary cursor-pointer select-none hover:underline">View match numbers</summary>
								<div class="flex flex-wrap gap-1.5 mt-1">
									<span v-for="(m, idx) in group.matches.slice(0, 5)" :key="m.matchNumber ?? `${group.groupNumber}-${idx}`" class="text-[0.7rem] py-0.5 px-1.5 bg-[var(--egs-content-hover)] rounded text-color-secondary">
										#{{ m.matchNumber }}
									</span>
									<span v-if="group.matches.length > 5" class="text-[0.7rem] text-color-secondary italic"> +{{ group.matches.length - 5 }} more </span>
								</div>
							</details>
						</div>
					</div>
				</div>

				<!-- All Past Dates Warning -->
				<div v-if="panelView !== 'form' && pastDatesInfo.allPast" class="flex flex-col items-start gap-2 py-4 px-4 bg-[var(--egs-content-hover)] text-color-secondary rounded-lg mb-4">
					<font-awesome-icon :icon="['fas', 'clock-rotate-left']" class="text-2xl text-color-secondary/60" />
					<div class="flex flex-col gap-1">
						<span class="font-semibold text-[0.9375rem] text-color">These dates are in the past</span>
						<span class="text-[0.8125rem] leading-relaxed">No need to set availability for dates that have already passed. Try selecting upcoming dates instead!</span>
					</div>
				</div>

				<!-- Some Past Dates Info -->
				<div v-else-if="panelView !== 'form' && pastDatesInfo.somePast" class="flex items-center gap-2 py-2.5 px-3 bg-orange-500/[0.12] text-orange-700 rounded-lg text-[0.8125rem] mb-4">
					<font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
					<span>
						{{ pastDatesInfo.pastCount }} {{ pastDatesInfo.pastCount === 1 ? 'date is' : 'dates are' }} in the past and will be skipped. Setting availability for {{ pastDatesInfo.futureCount }} upcoming
						{{ pastDatesInfo.futureCount === 1 ? 'date' : 'dates' }}.
					</span>
				</div>

				<!-- Keep offered availability and explicit blocks visible together. -->
				<AvailabilityEntryDetails v-if="panelView !== 'form' && selectedBlocks.length" :entries="selectedBlocks" :showDates="selectedDates.length > 1 || hasMultiDayBlocks" class="mb-4" />
				<AvailabilityEntryDetails v-if="panelView !== 'form' && hasExistingAvailability" :entries="existingAvailability" kind="available" :showDates="selectedDates.length > 1 || hasMultiDayBlocks" class="mb-4" />
				<div v-if="panelView !== 'form' && canClearBlocks" class="mb-4">
					<p class="mt-2 mb-0 text-xs text-color-secondary">Clears available and not available entries for the selected {{ selectedDates.length === 1 ? 'day' : 'days' }}.</p>
					<p v-if="hasMultiDayBlocks" class="mt-2 mb-0 text-xs text-color-secondary">Clearing removes entire entries, including any other dates or recurring days they cover.</p>
					<button
						type="button"
						class="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-surface bg-[var(--egs-content-background)] px-3 py-2.5 text-sm font-medium text-color-secondary transition-colors hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
						:disabled="saving || stores.availabilityStore.saving"
						@click="clearSelectedBlocks"
					>
						<font-awesome-icon :icon="['fas', 'trash']" />
						<span>{{ selectedDates.length === 1 ? 'Clear availability for the day' : 'Clear availability for selected days' }}</span>
					</button>
				</div>

				<!-- Divider -->
				<div v-if="panelView !== 'form' && !pastDatesInfo.allPast" class="h-px bg-surface my-4"></div>

				<!-- Only show action sections if there are future dates -->
				<template v-if="panelView !== 'form' && !pastDatesInfo.allPast">
					<!-- Scope Selector -->
					<div class="mb-5">
						<label class="block text-xs font-semibold uppercase tracking-wide text-color-secondary mb-2">Scope</label>
						<div class="flex gap-2">
							<button
								class="flex-1 py-2.5 px-3 border border-surface rounded-lg bg-[var(--egs-content-background)] text-color-secondary text-[0.8125rem] font-medium cursor-pointer transition-all hover:border-primary hover:text-primary"
								:class="{ '!bg-primary/10 !border-primary !text-primary': selectedScope === BLOCK_TYPES.AVAILABLE_GLOBAL }"
								@click="selectedScope = BLOCK_TYPES.AVAILABLE_GLOBAL"
							>
								All Groups
							</button>
							<button
								class="flex-1 py-2.5 px-3 border border-surface rounded-lg bg-[var(--egs-content-background)] text-color-secondary text-[0.8125rem] font-medium cursor-pointer transition-all hover:border-primary hover:text-primary"
								:class="{ '!bg-primary/10 !border-primary !text-primary': selectedScope === BLOCK_TYPES.AVAILABLE_GROUP }"
								@click="selectedScope = BLOCK_TYPES.AVAILABLE_GROUP"
							>
								Specific Group
							</button>
						</div>

						<!-- Group Selector (shown when Specific Group is selected) -->
						<Transition name="slide-down">
							<div v-if="selectedScope === BLOCK_TYPES.AVAILABLE_GROUP" class="mt-3">
								<EGS_Dropdown
									v-model="selectedGroupNumber"
									:options="userGroups"
									optionLabel="name"
									optionValue="groupNumber"
									placeholder="Select a group..."
									:disabled="groupsLoading || userGroups.length === 0"
									class="w-full"
									:class="{ 'dropdown-invalid': showGroupValidation && !selectedGroupNumber }"
								/>
								<small v-if="groupsLoading" class="block mt-1.5 text-color-secondary text-xs">Loading your groups…</small>
								<small v-else-if="userGroups.length === 0" class="block mt-1.5 text-color-secondary text-xs">No groups found for your account.</small>
								<small v-else-if="showGroupValidation && !selectedGroupNumber" class="block mt-1.5 text-red-500 text-xs">Please select a group to continue</small>
								<small v-else class="block mt-1.5 text-color-secondary text-xs">Choose a group to apply this availability only within that group.</small>
							</div>
						</Transition>
					</div>

					<!-- Quick Add Buttons -->
					<div class="mb-5">
						<label class="block text-xs font-semibold uppercase tracking-wide text-color-secondary mb-2">Quick Add Availability</label>
						<div class="grid grid-cols-3 gap-2.5">
							<button
								class="flex flex-col items-center gap-1.5 py-4 px-3 border-none rounded-xl bg-primary text-white cursor-pointer transition-colors hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
								:disabled="saving || !canSave"
								@click="quickAdd('all')"
							>
								<span class="text-[0.8125rem] font-semibold">All Day</span>
							</button>
							<button
								class="flex flex-col items-center gap-1.5 py-4 px-3 border border-surface rounded-xl bg-[var(--egs-content-background)] cursor-pointer transition-colors hover:border-primary hover:bg-[var(--egs-content-hover)] active:bg-[var(--egs-content-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
								:disabled="saving || !canSave"
								@click="quickAdd('am')"
							>
								<span class="text-[0.8125rem] font-semibold text-color">AM Only</span>
								<span class="text-[0.6875rem] text-color-secondary font-normal">6am - 12pm</span>
							</button>
							<button
								class="flex flex-col items-center gap-1.5 py-4 px-3 border border-surface rounded-xl bg-[var(--egs-content-background)] cursor-pointer transition-colors hover:border-primary hover:bg-[var(--egs-content-hover)] active:bg-[var(--egs-content-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
								:disabled="saving || !canSave"
								@click="quickAdd('pm')"
							>
								<span class="text-[0.8125rem] font-semibold text-color">PM Only</span>
								<span class="text-[0.6875rem] text-color-secondary font-normal">12pm - 11pm</span>
							</button>
						</div>
					</div>

					<!-- Custom Form Link -->
					<button
						class="flex items-center justify-between w-full py-3 px-3.5 border border-dashed border-surface rounded-lg bg-transparent text-color-secondary text-sm cursor-pointer transition-all hover:border-primary hover:text-primary hover:bg-primary/5"
						@click="openCustomForm"
					>
						<span>Set custom availability...</span>
						<font-awesome-icon :icon="['fas', 'arrow-right']" />
					</button>
				</template>
			</div>

			<!-- Footer -->
			<div v-if="panelView !== 'form'" class="flex gap-2.5 py-4 px-5 border-t border-surface bg-[var(--egs-content-hover)] flex-col" :class="{ '!flex-row': mobileMode }">
				<button
					class="w-full py-3 px-5 border border-surface rounded-lg bg-[var(--egs-content-background)] text-color-secondary text-sm font-medium cursor-pointer transition-all hover:bg-[var(--egs-content-hover)]"
					:class="{ '!w-auto !flex-1': mobileMode }"
					@click="close"
				>
					{{ pastDatesInfo.allPast ? 'Close' : 'Cancel' }}
				</button>
				<button
					v-if="!pastDatesInfo.allPast"
					class="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-red-300 bg-[var(--egs-content-background)] px-4 py-3 text-sm font-medium text-red-600 transition-all hover:border-red-400 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-950/50"
					:class="{ '!w-auto !flex-1': mobileMode }"
					:disabled="saving || !canSave"
					@click="markAsBlocked"
				>
					<font-awesome-icon :icon="['fas', 'ban']" />
					<span>Mark as Blocked</span>
				</button>
			</div>
		</div>
	</Transition>
</template>
